import React, { useEffect, useMemo } from "react";
import { Building2, AlertTriangle } from "lucide-react";

const StepChoosePaidEmployees = ({
  payrollData = [],
  selectedEmployees = [],
  setSelectedEmployees,
  handleEmployeeSelection,
  setSelectedAll,
  modalStyles,
  currentTheme,
  t,
}) => {
  // ── Only show PAID employees ──
  const paidPayrollData = useMemo(
    () => payrollData.filter((employee) => employee.status === "paid"),
    [payrollData],
  );

  const validSelectedEmployees = useMemo(
    () =>
      selectedEmployees.filter((emp) =>
        paidPayrollData.some((u) => u.id === emp.id),
      ),
    [selectedEmployees, paidPayrollData],
  );

  // ── Grouping by department ──
  const groupedByDepartment = useMemo(
    () =>
      paidPayrollData.reduce((acc, employee) => {
        const dept = employee.department || "Unknown";
        if (!acc[dept]) acc[dept] = [];
        acc[dept].push(employee);
        return acc;
      }, {}),
    [paidPayrollData],
  );

  const isGroupFullySelected = (employees) =>
    employees.every((emp) =>
      validSelectedEmployees.some((selected) => selected.id === emp.id),
    );

  const isGroupPartiallySelected = (employees) =>
    employees.some((emp) =>
      validSelectedEmployees.some((selected) => selected.id === emp.id),
    ) && !isGroupFullySelected(employees);

  const handleGroupSelection = (employees) => {
    const allSelected = isGroupFullySelected(employees);

    if (allSelected) {
      setSelectedEmployees((prev) =>
        prev.filter(
          (selected) => !employees.some((emp) => emp.id === selected.id),
        ),
      );
    } else {
      setSelectedEmployees((prev) => {
        const newEmployees = employees.filter(
          (emp) => !prev.some((selected) => selected.id === emp.id),
        );
        return [...prev, ...newEmployees];
      });
    }
  };

  const allSelected =
    paidPayrollData.length > 0 &&
    paidPayrollData.every((emp) =>
      validSelectedEmployees.some((selected) => selected.id === emp.id),
    );
  const someSelected =
    paidPayrollData.some((emp) =>
      validSelectedEmployees.some((selected) => selected.id === emp.id),
    ) && !allSelected;

  useEffect(() => {
    setSelectedAll(allSelected);
  }, [allSelected, setSelectedAll]);

  const handleSelectAllToggle = () => {
    if (paidPayrollData.length === 0) return;
    handleGroupSelection(paidPayrollData);
  };

  const renderEmployeeRow = (employee) => {
    const isSelected = validSelectedEmployees.some(
      (emp) => emp.id === employee.id,
    );

    return (
      <div
        key={employee.id}
        style={{
          ...modalStyles.employeeItem,
          paddingInlineStart: "64px",
          paddingInlineEnd: "24px",
          borderRadius: "10px",
          margin: "6px 0",
          background: isSelected
            ? `${currentTheme.colors.danger}10`
            : "transparent",
          border: isSelected
            ? `2px solid ${currentTheme.colors.danger}40`
            : `1px solid ${currentTheme.colors.borderLight}`,
          boxShadow: isSelected
            ? "0 2px 12px rgba(239,68,68,0.08)"
            : "none",
          transition: "all 0.2s",
        }}
        onClick={() => handleEmployeeSelection(employee)}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = isSelected
            ? `${currentTheme.colors.danger}20`
            : `${currentTheme.colors.primary}08`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = isSelected
            ? `${currentTheme.colors.danger}10`
            : "transparent";
        }}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => {}}
          style={{
            accentColor: currentTheme.colors.danger,
            width: "18px",
            height: "18px",
            marginInlineEnd: "12px",
            cursor: "pointer",
          }}
        />

        <div style={{ flex: 1 }}>
          <div
            style={{
              fontWeight: "700",
              color: currentTheme.colors.textPrimary,
              fontSize: "15px",
              marginBottom: "2px",
            }}
          >
            {employee.employeeName}
          </div>
          <div
            style={{
              fontSize: "13px",
              color: currentTheme.colors.textSecondary,
            }}
          >
            {employee.department}
          </div>
        </div>
        <div style={{ textAlign: "end" }}>
          <div
            style={{
              fontWeight: "700",
              color: currentTheme.colors.danger,
              fontSize: "15px",
            }}
          >
            {employee.netSalary.toLocaleString()} {t("currency")}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: currentTheme.colors.textMuted,
            }}
          >
            {employee.paymentDate
              ? new Date(employee.paymentDate).toLocaleDateString("en-US")
              : "-"}
          </div>
        </div>
      </div>
    );
  };

  const renderDepartmentGroup = (department, employees) => {
    const isFullySelected = isGroupFullySelected(employees);
    const isPartiallySelected = isGroupPartiallySelected(employees);

    return (
      <div key={department} style={{ marginBottom: "16px" }}>
        <div
          onClick={() => handleGroupSelection(employees)}
          style={{
            display: "flex",
            alignItems: "center",
            padding: "12px 16px",
            background: isFullySelected
              ? `${currentTheme.colors.danger}15`
              : `${currentTheme.colors.cardBackground}`,
            border: `1px solid ${currentTheme.colors.borderLight}`,
            borderRadius: "10px",
            cursor: "pointer",
            marginBottom: "8px",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = isFullySelected
              ? `${currentTheme.colors.danger}20`
              : `${currentTheme.colors.primary}08`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = isFullySelected
              ? `${currentTheme.colors.danger}15`
              : `${currentTheme.colors.cardBackground}`;
          }}
        >
          <input
            type="checkbox"
            checked={isFullySelected}
            ref={(input) => {
              if (input) input.indeterminate = isPartiallySelected;
            }}
            onChange={() => {}}
            style={{
              accentColor: currentTheme.colors.danger,
              width: "18px",
              height: "18px",
              marginInlineEnd: "12px",
              cursor: "pointer",
            }}
          />
          <Building2 size={16} style={{ marginInlineEnd: "8px" }} />
          <span
            style={{
              fontWeight: "700",
              color: currentTheme.colors.textPrimary,
              fontSize: "15px",
              flex: 1,
            }}
          >
            {department}
          </span>
          <span
            style={{
              fontSize: "13px",
              color: currentTheme.colors.textSecondary,
              background: `${currentTheme.colors.primary}15`,
              padding: "4px 8px",
              borderRadius: "6px",
            }}
          >
            {employees.length} {t("employees")}
          </span>
        </div>
        <div style={{ paddingInlineStart: "8px" }}>
          {employees.map((employee) => renderEmployeeRow(employee))}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: "0 24px 24px" }}>
      {/* Header with warning */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "16px",
          background: `${currentTheme.colors.danger}10`,
          border: `1px solid ${currentTheme.colors.danger}30`,
          borderRadius: "10px",
          marginBottom: "24px",
        }}
      >
        <AlertTriangle size={20} color={currentTheme.colors.danger} />
        <div>
          <div
            style={{
              fontWeight: "700",
              color: currentTheme.colors.danger,
              fontSize: "14px",
            }}
          >
            {t("unpayWarning")}
          </div>
          <div
            style={{
              fontSize: "13px",
              color: currentTheme.colors.textSecondary,
              marginTop: "4px",
            }}
          >
            {t("unpayWarningDescription")}
          </div>
        </div>
      </div>

      {paidPayrollData.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "40px 20px",
            color: currentTheme.colors.textMuted,
          }}
        >
          <div style={{ fontSize: "16px", fontWeight: "600" }}>
            {t("noPaidEmployees")}
          </div>
          <div style={{ fontSize: "14px", marginTop: "8px" }}>
            {t("allEmployeesPending")}
          </div>
        </div>
      ) : (
        <>
          {/* Select All */}
          <div
            onClick={handleSelectAllToggle}
            style={{
              display: "flex",
              alignItems: "center",
              padding: "12px 16px",
              background: `${currentTheme.colors.primary}08`,
              border: `2px solid ${
                allSelected ? currentTheme.colors.primary : "transparent"
              }`,
              borderRadius: "10px",
              cursor: "pointer",
              marginBottom: "20px",
              fontWeight: "700",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = currentTheme.colors.primary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = allSelected
                ? currentTheme.colors.primary
                : "transparent";
            }}
          >
            <input
              type="checkbox"
              checked={allSelected}
              ref={(input) => {
                if (input) input.indeterminate = someSelected;
              }}
              onChange={() => {}}
              style={{
                accentColor: currentTheme.colors.danger,
                width: "18px",
                height: "18px",
                marginInlineEnd: "12px",
                cursor: "pointer",
              }}
            />
            <span style={{ flex: 1 }}>{t("selectAll")}</span>
            <span
              style={{
                fontSize: "13px",
                color: currentTheme.colors.textSecondary,
              }}
            >
              {validSelectedEmployees.length}/{paidPayrollData.length}
            </span>
          </div>

          {/* Departments */}
          {Object.entries(groupedByDepartment)
            .sort()
            .map(([dept, employees]) =>
              renderDepartmentGroup(dept, employees),
            )}
        </>
      )}
    </div>
  );
};

export default StepChoosePaidEmployees;
